import { Injectable } from '@angular/core';

import { jwtDecode } from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';
import { Apps } from '../enums/enum';
import { BaseUserModel, UserRolesModel } from '../models/baseUser.model';
import { ErrorHandlerService } from './error-handler.service';
import { SharedUserApiService } from './shared-user-api.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(
    private cookieService: CookieService,
    private sharedUserApiService: SharedUserApiService,
    private errorHandlerService: ErrorHandlerService
  ) {}

  LoginUrl: string = environment.companyApiUrl;
  public userRoles: UserRolesModel[] = [];
  public Roles: UserRolesModel[] = [];
  private userRolesSubject = new BehaviorSubject<UserRolesModel[]>([]);
  private rolesLoaded = false;
  private userData: BaseUserModel | null = null;

  async restoreUserSession(): Promise<void> {
    const accessToken = this.cookieService.get('accessToken');
    const refreshToken = this.cookieService.get('refreshToken');
    if (accessToken || refreshToken) {
      if (!this.isInternalUser(accessToken)) {
        this.clearAllStorage();
        this.redirectToLogin();
        return;
      }
      try {
        if (accessToken) {
          await this.saveUserInfo(accessToken);
          return;
        }
        return;
      } catch (error) {
        this.clearAllStorage();
        this.redirectToLogin();
      }
    } else {
      this.clearAllStorage();
      this.redirectToLogin();
    }
  }

  getAuthToken(): string | null {
    return this.cookieService.get('accessToken') || null;
  }

  authorizeUser(): void {
    const accessToken = this.cookieService.get('accessToken');
    if (!accessToken || !this.isInternalUser(accessToken)) {
      this.redirectToLogin();
    }
  }

  isAuthenticated(): boolean {
    const accessToken = this.cookieService.get('accessToken');
    const userDetails = this.getUserFromLocalStorage();
    return !!(accessToken && userDetails && this.isInternalUser(accessToken));
  }

  /**
   * Inventory Management is an internal staff portal. External accounts
   * (consignors, donors) have their own portals, so an External-origin SSO
   * token must never open a session here even though the cookie is visible on
   * the shared domain.
   */
  isInternalUser(token: string | null): boolean {
    if (!token) {
      return false;
    }
    return this.decodeJwt(token)?.userorigin === 'Internal';
  }
  redirectToLogin(): void {
    window.location.replace(`${environment.companyUrl}/shared/login`);
  }
  checkAuthentication(): boolean {
    if (!this.isAuthenticated()) {
      this.redirectToLogin();
      return false;
    }
    return true;
  }

  decodeJwt(token: string): any {
    try {
      const decodedToken = jwtDecode(token);
      return this.removeCustomPrefix(decodedToken);
    } catch (error) {
      return null;
    }
  }

  removeCustomPrefix(tokenData: Record<string, any>): Record<string, any> {
    const cleanedData: Record<string, any> = {};
    Object.keys(tokenData).forEach(key => {
      const newKey = key.startsWith('custom:') ? key.replace('custom:', '') : key;
      cleanedData[newKey] = tokenData[key];
    });
    return cleanedData;
  }

  async saveUserInfo(token: string) {
    if (!token) {
      throw new Error('Token is required to save user info');
    }

    const decodedToken = this.decodeJwt(token);
    if (decodedToken) {
      const { userid, companyid, fullname, role } = decodedToken;
      const user = new BaseUserModel();
      user.userId = userid;
      user.companyId = companyid;
      user.fullName = fullname;
      user.userRole = role;
      const userJson = JSON.stringify(user);
      try {
        const response = await firstValueFrom(this.sharedUserApiService.getUserLiteById(user.userId));
        if (response) {
          const transformedResponse = this.transformResponse(response);
          const userObj = JSON.stringify(transformedResponse);
          localStorage.setItem('user', userObj);
        }
      } catch (error) {
        this.errorHandlerService.handleApiError(error);
      }
    } else {
      throw new Error('Failed to decode token');
    }
  }

  getUserFromLocalStorage(): any {
    const userDetails = localStorage.getItem('user');
    if (userDetails) {
      try {
        const user = JSON.parse(userDetails);
        const activeWarehouseId = localStorage.getItem('imsSelectedWarehouseId');
        if (activeWarehouseId) {
          user.imsDefaultWarehouseId = Number(activeWarehouseId);
        }
        return user;
      } catch (error) {
        console.error('Error parsing userDetails from localStorage:', error);
        return null;
      }
    }
    return null;
  }

  logout(): void {
    this.clearAllStorage();

    this.redirectToLogin();
  }

  private clearAllStorage(): void {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    localStorage.clear();
    sessionStorage.clear();

    if (isLocal) {
      this.cookieService.deleteAll('/');
      this.cookieService.set('logoutEvent', '1', 1 / 144, '/');
    } else {
      const domain = environment.domainName;
      this.cookieService.deleteAll('/', domain);
      this.cookieService.set('logoutEvent', '1', 1 / 144, '/', domain);
    }
  }

  transformResponse(newResponse: any): any {
    return {
      email: newResponse.email,
      userName: `${newResponse.firstName} ${newResponse.middleName || ''} ${newResponse.lastName}`.trim(),
      company: newResponse.company?.name || null,
      userRole: this.userRoles?.[0]?.name || 'User',
      userId: newResponse.id,
      companyId: newResponse.company?.id || 0,
      defaultModuleID: newResponse.defaultModuleId,
      storeID: newResponse.defaultStoreId || 0,
      fullName: `${newResponse.firstName} ${newResponse.middleName || ''} ${newResponse.lastName}`.trim(),
      isNewUser: false,
      isActive: newResponse.status === 1,
      displayName: newResponse.displayName,
      profileImgUrl: newResponse.profileFileUrl,
      appID: newResponse.apps?.[0]?.id || null,
      outletID: newResponse.defaultOutletId || 0,
      apps: newResponse.apps,
      imsDefaultWarehouseId: newResponse.imsDefaultWarehouseId || 0,
      defaultWarehouseId: newResponse.defaultWarehouseId || 0,
      productionScheduleType: newResponse.productionScheduleType ?? newResponse.ProductionScheduleType ?? null,
      isTpmPresent: !!newResponse.apps?.find((app: { id: number }) => app.id == Apps.TPMPortal),
      isPosPresent: !!newResponse.apps?.find((app: { id: number }) => app.id == Apps.POS),
      isDonationPresent: !!newResponse.apps?.find((app: { id: number }) => app.id == Apps.Donation),
      isListerPresent: !!newResponse.apps?.find((app: { id: number }) => app.id == Apps.Lister),
      isConsignmentPresent: !!newResponse.apps?.find((app: { id: number }) => app.id == Apps.Consignment),
      isCompanyPortalPresent: !!newResponse.apps?.find((app: { id: number }) => app.id == Apps.CompanyPortal)
    };
  }
  async getUserRolesandPermission(): Promise<UserRolesModel[]> {
    if (this.Roles.length > 0) {
      return this.Roles;
    }
    this.userData = this.getUserFromLocalStorage();
    if (this.userData) {
      try {
        const data = await firstValueFrom(this.sharedUserApiService.getUserRoleDetails(this.userData.userId));

        if (data) {
          const appRoles = data as UserRolesModel[];
          this.userRolesSubject.next(appRoles);

          this.Roles = appRoles;
          this.rolesLoaded = true;
          return appRoles;
        }
      } catch (error) {
        this.errorHandlerService.handleApiError(error);
      }
    }
    return [];
  }

  hasAppAccess(appName: string): boolean {
    if (!appName) return false;
    const user = this.getUserFromLocalStorage();
    if (!user || !user.apps) return false;
    return user.apps.some((a: any) => a.name?.toLowerCase() === appName.toLowerCase());
  }
}
